import { c as _c } from "react/compiler-runtime";
import { CurriculumItemType } from '@udemy-v2/browse-course';
import { useI18n } from '@udemy-v2/i18n';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useContentLengthFormatter } from '@/features/course-landing-page/hooks/use-content-length-formatter.react-component';
import { CourseAccreditationType } from '@udemy-v2/browse-course';
import { IconName } from '@udemy-v2/icons';
import { IncentiveType } from '@/features/course-landing-page/events/events';
import { useCourseIncludesRolePlay } from '@/features/course-landing-page/components/course-includes/hooks/use-course-includes-role-play';
import { useClpPurchaseContext } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { useCommonData, getUserTypes } from '@udemy-v2/common-data';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
export enum SupportedIncentives {
  IS_FREE_SEO_EXP,
  VIDEO_CONTENT_LENGTH,
  AUDIO_CONTENT_LENGTH,
  NUM_ARTICLES,
  NUM_QUIZZES,
  NUM_PRACTICE_TESTS,
  NUM_CODING_EXERCISES,
  NUM_ADDITIONAL_RESOURCES,
  HAS_LIFETIME_ACCESS,
  DEVICES_ACCESS,
  HAS_ASSIGNMENTS,
  HAS_CERTIFICATE,
  NUM_CPE_CREDITS,
  PLACEMENT,
  HAS_CLOSED_CAPTIONS,
  HAS_AUDIO_DESCRIPTION,
  ROLE_PLAY,
  GRADED_ROLE_PLAY,
  HAS_IN_COURSE_LABS,
}

/**
 * Interface for course incentives with priority
 */
export interface CourseIncentive<T extends string | boolean | number> {
  value: T;
  priority: number;
  iconGlyph?: IconName;
  text?: string;
  type?: IncentiveType;
  count?: number;
}

/**
 * Priority mapping for each incentive type
 */
export const IncentivePriorities: Record<SupportedIncentives, number> = {
  [SupportedIncentives.ROLE_PLAY]: 0,
  // Same priority as ROLE_PLAY; stable sort keeps the standard Role Play row first
  [SupportedIncentives.GRADED_ROLE_PLAY]: 0,
  [SupportedIncentives.VIDEO_CONTENT_LENGTH]: 1,
  [SupportedIncentives.AUDIO_CONTENT_LENGTH]: 2,
  [SupportedIncentives.NUM_CODING_EXERCISES]: 3,
  [SupportedIncentives.NUM_QUIZZES]: 4,
  [SupportedIncentives.NUM_PRACTICE_TESTS]: 5,
  [SupportedIncentives.HAS_ASSIGNMENTS]: 6,
  [SupportedIncentives.NUM_ARTICLES]: 7,
  [SupportedIncentives.NUM_ADDITIONAL_RESOURCES]: 8,
  [SupportedIncentives.DEVICES_ACCESS]: 9,
  [SupportedIncentives.HAS_LIFETIME_ACCESS]: 10,
  [SupportedIncentives.NUM_CPE_CREDITS]: 11,
  [SupportedIncentives.HAS_CLOSED_CAPTIONS]: 12,
  [SupportedIncentives.HAS_AUDIO_DESCRIPTION]: 13,
  [SupportedIncentives.HAS_CERTIFICATE]: 14,
  [SupportedIncentives.IS_FREE_SEO_EXP]: 15,
  [SupportedIncentives.PLACEMENT]: 16,
  [SupportedIncentives.HAS_IN_COURSE_LABS]: 17
};

/**
 * Hook that returns the applicable incentives for the current course.
 * Processes raw incentive data into display-ready format.
 * @returns Array of CourseIncentive objects ready for display
 */
export function useCourseIncentives() {
  const $ = _c(1);
  const {
    gettext,
    ninterpolate,
    interpolate
  } = useI18n();
  const formatContentLength = useContentLengthFormatter();
  const course = useClpCourse();
  const {
    showLifetimeAccess,
    showLifetimeAccessInBuyBox
  } = useClpPurchaseContext();
  const hasOrganization = useUserHasOrganization();
  const commonData = useCommonData();
  const {
    isMXUser
  } = getUserTypes(commonData);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["clp_labs_incentive"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t0);
  const isClpLabsIncentiveEnabled = featureVariantAssignments.get("clp_labs_incentive")?.configuration?.enableClpLabsIncentive === true;
  const processedIncentives = [];
  if (course.isFree && !hasOrganization) {
    processedIncentives.push({
      value: true,
      priority: IncentivePriorities[SupportedIncentives.IS_FREE_SEO_EXP],
      type: IncentiveType.SUBSCRIPTION_DIFFERENTIATION,
      text: gettext("Free course"),
      iconGlyph: "gift"
    });
  }
  if (course.durationVideoContent && course.durationVideoContent > 0) {
    const videoInMinutes = course.durationVideoContent / 60;
    if (videoInMinutes > 0) {
      const formattedLength = formatContentLength(videoInMinutes);
      processedIncentives.push({
        value: formattedLength,
        priority: IncentivePriorities[SupportedIncentives.VIDEO_CONTENT_LENGTH],
        type: IncentiveType.VIDEO_CONTENT_LENGTH,
        text: interpolate(gettext("%(videoContentLength)s on-demand video"), {
          videoContentLength: formattedLength
        }, true),
        iconGlyph: "video"
      });
    }
  }
  if (course.durationAudioContent && course.durationAudioContent > 0) {
    const audioInMinutes = course.durationAudioContent / 60;
    if (audioInMinutes > 0) {
      const formattedLength_0 = formatContentLength(audioInMinutes);
      processedIncentives.push({
        value: formattedLength_0,
        priority: IncentivePriorities[SupportedIncentives.AUDIO_CONTENT_LENGTH],
        type: IncentiveType.AUDIO_CONTENT_LENGTH,
        text: interpolate(gettext("%(audioContentLength)s on-demand audio"), {
          audioContentLength: formattedLength_0
        }, true),
        iconGlyph: "audio"
      });
    }
  }
  if (course.curriculum) {
    const articleItems = course.curriculum.sections.flatMap(_temp2);
    if (articleItems.length > 0) {
      const numArticles = articleItems.length;
      processedIncentives.push({
        value: numArticles,
        priority: IncentivePriorities[SupportedIncentives.NUM_ARTICLES],
        type: IncentiveType.NUM_ARTICLES,
        text: ninterpolate("%(count)s article", "%(count)s articles", numArticles, {
          count: numArticles
        }),
        count: numArticles,
        iconGlyph: "article"
      });
    }
    const practiceTestItems = course.curriculum.sections.flatMap(_temp4);
    if (practiceTestItems.length > 0) {
      const numPracticeTests = practiceTestItems.length;
      processedIncentives.push({
        value: numPracticeTests,
        priority: IncentivePriorities[SupportedIncentives.NUM_PRACTICE_TESTS],
        type: IncentiveType.NUM_PRACTICE_TESTS,
        text: ninterpolate("%(count)s practice test", "%(count)s practice tests", numPracticeTests, {
          count: numPracticeTests
        }),
        count: numPracticeTests,
        iconGlyph: "quiz"
      });
    }
    const numCodingExercises = course.curriculum?.contentCounts?.codingExercisesCount || 0;
    if (numCodingExercises > 0) {
      processedIncentives.push({
        value: numCodingExercises,
        priority: IncentivePriorities[SupportedIncentives.NUM_CODING_EXERCISES],
        type: IncentiveType.NUM_CODING_EXERCISES,
        text: ninterpolate("%(count)s coding exercise", "%(count)s coding exercises", numCodingExercises, {
          count: numCodingExercises
        }),
        count: numCodingExercises,
        iconGlyph: "code"
      });
    }
    const numAdditionalResources = course.curriculum?.contentCounts?.additionalResourcesCount || 0;
    if (numAdditionalResources > 0) {
      processedIncentives.push({
        value: numAdditionalResources,
        priority: IncentivePriorities[SupportedIncentives.NUM_ADDITIONAL_RESOURCES],
        type: IncentiveType.NUM_RESOURCES,
        text: ninterpolate("%(count)s downloadable resource", "%(count)s downloadable resources", numAdditionalResources, {
          count: numAdditionalResources
        }),
        count: numAdditionalResources,
        iconGlyph: "downloadable-resource"
      });
    }
  }
  if (showLifetimeAccess && !showLifetimeAccessInBuyBox) {
    processedIncentives.push({
      value: true,
      priority: IncentivePriorities[SupportedIncentives.HAS_LIFETIME_ACCESS],
      type: IncentiveType.HAS_LIFETIME_ACCESS,
      text: gettext("Full lifetime access"),
      iconGlyph: "lifetime"
    });
  }
  const isPracticeTestCourse = course.isPracticeTestCourse;
  const deviceText = !isPracticeTestCourse ? gettext("Access on mobile and TV") : gettext("Access on mobile");
  processedIncentives.push({
    value: deviceText,
    priority: IncentivePriorities[SupportedIncentives.DEVICES_ACCESS],
    type: IncentiveType.DEVICES_ACCESS,
    text: deviceText,
    iconGlyph: "mobile"
  });
  const hasAssignments = course.curriculum?.sections.some(_temp6);
  if (hasAssignments) {
    processedIncentives.push({
      value: true,
      priority: IncentivePriorities[SupportedIncentives.HAS_ASSIGNMENTS],
      type: IncentiveType.HAS_ASSIGNMENTS,
      text: gettext("Assignments"),
      iconGlyph: "assignment-outline"
    });
  }
  if (course.hasCertificate && !course.isFree) {
    processedIncentives.push({
      value: true,
      priority: IncentivePriorities[SupportedIncentives.HAS_CERTIFICATE],
      type: IncentiveType.HAS_CERTIFICATE,
      text: gettext("Certificate of completion"),
      iconGlyph: "trophy"
    });
  }
  const cpeCredits = course.accreditations ? course.accreditations.filter(_temp7).reduce(_temp8, 0) : 0;
  if (cpeCredits > 0) {
    processedIncentives.push({
      value: cpeCredits,
      priority: IncentivePriorities[SupportedIncentives.NUM_CPE_CREDITS],
      type: IncentiveType.NUM_CPE_CREDITS,
      text: ninterpolate("%(count)s CPE credit", "%(count)s CPE credits", cpeCredits, {
        count: cpeCredits
      }),
      count: cpeCredits,
      iconGlyph: "medal"
    });
  }
  if (course.captions && course.captions.length > 0) {
    processedIncentives.push({
      value: true,
      priority: IncentivePriorities[SupportedIncentives.HAS_CLOSED_CAPTIONS],
      type: IncentiveType.HAS_CLOSED_CAPTIONS,
      text: gettext("Closed captions"),
      iconGlyph: "captions-outline"
    });
  }
  const hasAudioDescription = course.captions?.some(_temp9);
  if (hasAudioDescription) {
    processedIncentives.push({
      value: true,
      priority: IncentivePriorities[SupportedIncentives.HAS_AUDIO_DESCRIPTION],
      type: IncentiveType.HAS_AUDIO_DESCRIPTION,
      text: gettext("Audio descriptions"),
      iconGlyph: "ad-audio-outline"
    });
  }
  const inCourseLabsCount = course.curriculum?.contentCounts?.inCourseLabsCount ?? 0;
  if (isMXUser && isClpLabsIncentiveEnabled && inCourseLabsCount > 0) {
    processedIncentives.push({
      value: true,
      priority: IncentivePriorities[SupportedIncentives.HAS_IN_COURSE_LABS],
      type: IncentiveType.HAS_IN_COURSE_LABS,
      text: gettext("Optional: hands-on practice for an additional fee"),
      iconGlyph: "labs"
    });
  }
  const {
    hasRolePlay,
    numRolePlays,
    hasGradedRolePlay,
    numGradedRolePlays
  } = useCourseIncludesRolePlay(course);
  if (hasRolePlay) {
    processedIncentives.push({
      value: numRolePlays,
      priority: IncentivePriorities[SupportedIncentives.ROLE_PLAY],
      type: IncentiveType.ROLE_PLAY,
      text: ninterpolate("%(count)s Role Play", "%(count)s Role Plays", numRolePlays, {
        count: numRolePlays
      }),
      count: numRolePlays,
      iconGlyph: "people"
    });
  }
  if (hasGradedRolePlay) {
    processedIncentives.push({
      value: numGradedRolePlays,
      priority: IncentivePriorities[SupportedIncentives.GRADED_ROLE_PLAY],
      type: IncentiveType.GRADED_ROLE_PLAY,
      text: ninterpolate("%(count)s Graded Role Play", "%(count)s Graded Role Plays", numGradedRolePlays, {
        count: numGradedRolePlays
      }),
      count: numGradedRolePlays,
      iconGlyph: "people"
    });
  }
  return processedIncentives.sort(_temp10);
}
function _temp10(a, b) {
  return a.priority - b.priority;
}
function _temp9(caption) {
  return caption.locale === "audio-description";
}
function _temp8(sum, accreditation_0) {
  return sum + accreditation_0.creditCount;
}
function _temp7(accreditation) {
  return accreditation.type === CourseAccreditationType.CPE;
}
function _temp6(section_1) {
  return section_1.items.some(_temp5);
}
function _temp5(item_1) {
  return item_1.type === CurriculumItemType.PRACTICE_ASSIGNMENT || item_1.type === CurriculumItemType.PRACTICE_TEST;
}
function _temp4(section_0) {
  return section_0.items.filter(_temp3);
}
function _temp3(item_0) {
  return item_0.type === CurriculumItemType.PRACTICE_TEST;
}
function _temp2(section) {
  return section.items.filter(_temp);
}
function _temp(item) {
  return item.type === CurriculumItemType.ARTICLE_LECTURE;
}